import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { IoIosClose } from "react-icons/io";
import {
  deliveryArrived,
  resolveComplaint,
  setAcceptOrder,
  setOrderDeliver,
  setReadyToDeliver,
} from "../../../../services/oms";
import {
  formatPickupDisplayLabel,
  formatPickupScheduleApi,
  getPickupSlotMoments,
} from "../../../../utils/pickupScheduleSlots";

const PICKUP_SLOT_COUNT = 10;

const MODAL_BOX_SX = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  maxWidth: "100%",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 4,
  display: "flex",
  flexDirection: "column",
  maxHeight: "85vh",
  overflow: "hidden",
  p: 0,
};

const scrollBodySx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

const TITLE_BY_ACTION = {
  accept: "Terima pesanan?",
  "ready-to-deliver": "Tandai siap dikirim?",
  "ready-to-deliver-express": "Tandai siap dikirim?",
  reject: "Batalkan pesanan?",
  delivered: "Tandai telah sampai?",
  "resolve-complaint": "Selesaikan komplain?",
};

const PRIMARY_LABEL_BY_ACTION = {
  accept: "Terima",
  "ready-to-deliver": "Tandai Siap Dikirim",
  "ready-to-deliver-express": "Kirim Sekarang",
  reject: "Batalkan",
  delivered: "Tandai Telah Sampai",
  "resolve-complaint": "Selesaikan Komplain",
};

const ModalConfirmStatus = ({
  open,
  setOpen,
  data,
  action,
  refetch,
  onProceedToReject,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [courirName, setCourirName] = useState("");
  const [courirNumber, setCourirNumber] = useState("");

  const isChooseCourier = action === "choose-courier";
  const isReadyToDeliverExpress = action === "ready-to-deliver-express";

  const pickupSlots = useMemo(() => {
    if (!open || !isReadyToDeliverExpress) return [];
    return getPickupSlotMoments(PICKUP_SLOT_COUNT);
  }, [open, isReadyToDeliverExpress]);

  useEffect(() => {
    if (open && isReadyToDeliverExpress && pickupSlots.length) {
      setSelectedSchedule(formatPickupScheduleApi(pickupSlots[0]));
    } else if (!open || !isReadyToDeliverExpress) {
      setSelectedSchedule("");
    }
  }, [open, isReadyToDeliverExpress, pickupSlots]);

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
    setSelectedSchedule("");
    setCourirName("");
    setCourirNumber("");
  };

  const isValidIndonesianPhoneNumber = (value) => {
    if (!value) return false;
    const normalized = value.replace(/\s|-/g, "");
    return /^(08\d{8,12}|628\d{8,12})$/.test(normalized);
  };

  const { mutate: mutateAcceptOrder, isLoading: isAcceptingOrder } =
    useMutation({
      mutationFn: setAcceptOrder,
      onError: (err) => setErrorMessage(err.id),
      onSuccess: () => {
        setSnackbarMessage("Pesanan berhasil diterima");
        setOpenSnackbar(true);
        refetch();
        handleClose();
      },
    });

  const { mutate: mutateReadyToDeliver, isLoading: isSubmittingReady } =
    useMutation({
      mutationFn: setReadyToDeliver,
      onError: (err) => setErrorMessage(err.id),
      onSuccess: () => {
        setSnackbarMessage("Pesanan berhasil ditandai siap dikirim");
        setOpenSnackbar(true);
        refetch();
        handleClose();
      },
    });

  const { mutate: mutateOrderDeliver, isLoading: isSubmittingDeliver } =
    useMutation({
      mutationFn: setOrderDeliver,
      onError: (err) =>
        setErrorMessage(err?.id || err?.message || "Gagal mengirim pesanan"),
      onSuccess: () => {
        setSnackbarMessage("Pesanan berhasil dikirim");
        setOpenSnackbar(true);
        refetch();
        handleClose();
      },
    });

  const { mutate: mutateOrderDelivered, isLoading: isSubmittingDelivered } =
    useMutation({
      mutationFn: deliveryArrived,
      onError: (err) =>
        setErrorMessage(
          err?.id || err?.message || "Gagal menandai pesanan telah sampai",
        ),
      onSuccess: () => {
        setSnackbarMessage("Pesanan berhasil ditandai telah sampai");
        setOpenSnackbar(true);
        refetch();
        handleClose();
      },
    });

  const { mutate: mutateOrderResolve, isLoading: isSubmittingResolve } =
    useMutation({
      mutationFn: resolveComplaint,
      onError: (err) =>
        setErrorMessage(
          err?.id || err?.message || "Gagal menyelesaikan komplain pesanan",
        ),
      onSuccess: () => {
        setSnackbarMessage("Pesanan berhasil diselesaikan");
        setOpenSnackbar(true);
        refetch();
        handleClose();
      },
    });

  const isPrimaryLoading =
    isAcceptingOrder ||
    isSubmittingReady ||
    isSubmittingDeliver ||
    isSubmittingDelivered ||
    isSubmittingResolve;

  const handlePrimaryAction = () => {
    if (!data?.order_id) return;

    if (action === "accept") {
      mutateAcceptOrder({
        order_id: data.order_id,
        partner_code: data.platform_code ?? "PSEF",
      });
      return;
    }

    if (action === "ready-to-deliver") {
      mutateReadyToDeliver({
        order_id: data.order_id,
      });
      return;
    }

    if (action === "ready-to-deliver-express") {
      if (!selectedSchedule) return;
      mutateReadyToDeliver({
        order_id: data.order_id,
        schedule: selectedSchedule,
      });
      return;
    }

    if (action === "choose-courier") {
      const trimmedName = courirName.trim();

      if (!trimmedName) {
        setErrorMessage("Nama kurir wajib diisi");
        return;
      }

      if (!isValidIndonesianPhoneNumber(courirNumber)) {
        setErrorMessage(
          "Nomor HP kurir tidak valid. Gunakan format 08... atau 628...",
        );
        return;
      }

      mutateOrderDeliver({
        order_id: data.order_id,
        partner_code: data.platform_code ?? "PSEF",
        courier_name: trimmedName,
        courier_number: courirNumber.replace(/\s|-/g, ""),
      });
      return;
    }

    if (action === "reject") {
      onProceedToReject?.(data);
      handleClose();
    }

    if (action === "delivered") {
      mutateOrderDelivered({
        order_id: data.order_id,
      });
    }

    if (action === "resolve-complaint") {
      mutateOrderResolve({
        order_id: data.order_id,
      });
    }
  };

  const primaryDisabled =
    isPrimaryLoading ||
    (isReadyToDeliverExpress && !selectedSchedule) ||
    (isChooseCourier &&
      (!courirName.trim() || !isValidIndonesianPhoneNumber(courirNumber))) ||
    !data?.order_id;

  const primaryButtonClass =
    action === "reject"
      ? "bg-red-500 hover:bg-red-700 border-red-500 hover:border-red-700"
      : "bg-blue-500 hover:bg-blue-700 border-blue-500 hover:border-blue-700";

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={MODAL_BOX_SX} className="text-left text-sm w-3/4 lg:w-1/3">
          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              pt: 3,
              pb: 2,
              borderBottom: "2px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <div className="flex justify-between items-center gap-2">
              <p className="font-semibold text-lg pr-2">
                {TITLE_BY_ACTION[action] ?? ""}
              </p>
              <IoIosClose
                className="hover:text-red-700 cursor-pointer w-[24px] h-[24px] shrink-0 hover:bg-gray-100 rounded-md transition-transform duration-300 ease-in-out hover:rotate-90"
                onClick={handleClose}
                aria-label="Tutup"
              />
            </div>
          </Box>

          <Box sx={{ ...scrollBodySx, px: 3, py: 3 }}>
            <div>
              Apakah Anda yakin ingin mengubah status pesanan{" "}
              <span className="font-semibold">{data?.order_code}</span> ini?
            </div>

            {isReadyToDeliverExpress ? (
              <Box sx={{ width: "100%", mx: 0 }}>
                <p className="text-sm font-medium text-gray-800 mb-3 mt-4">
                  Silahkan pilih jam pickup
                </p>
                <FormControl
                  component="fieldset"
                  variant="standard"
                  sx={{ width: "100%", m: 0 }}
                >
                  <RadioGroup
                    name="pickup-schedule"
                    value={selectedSchedule}
                    onChange={(e) => setSelectedSchedule(e.target.value)}
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      alignItems: "stretch",
                    }}
                  >
                    {pickupSlots.map((slot) => {
                      const value = formatPickupScheduleApi(slot);
                      return (
                        <FormControlLabel
                          key={value}
                          value={value}
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              sx={{ p: 0.75, mr: 1 }}
                            />
                          }
                          label={formatPickupDisplayLabel(slot)}
                          sx={{
                            m: 0,
                            mx: 0,
                            my: 0,
                            ml: 0,
                            mr: 0,
                            py: 1.25,
                            px: 2,
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem",
                              textAlign: "left",
                              flex: "0 1 auto",
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </Box>
            ) : null}

            {isChooseCourier ? (
              <div className="mt-4 flex flex-col gap-3">
                <TextField
                  fullWidth
                  label="Nama Kurir"
                  placeholder="Masukkan nama kurir"
                  value={courirName}
                  onChange={(e) => {
                    setErrorMessage("");
                    setCourirName(e.target.value);
                  }}
                  size="small"
                />

                <TextField
                  fullWidth
                  label="Nomor HP Kurir"
                  placeholder="08xxxxxxxxxx atau 628xxxxxxxxxx"
                  value={courirNumber}
                  onChange={(e) => {
                    const onlyNumber = e.target.value.replace(/[^\d]/g, "");
                    setErrorMessage("");
                    setCourirNumber(onlyNumber);
                  }}
                  size="small"
                />
              </div>
            ) : null}

            {errorMessage ? (
              <p className="text-red-500 text-sm pt-2">{errorMessage}</p>
            ) : null}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              py: 3,
              pt: 2,
              borderTop: "2px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <div className="w-full flex items-center justify-end gap-2">
              <button
                className="py-1 px-3 font-medium border-2 rounded-lg hover:bg-gray-100 cursor-pointer w-full"
                onClick={handleClose}
                type="button"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={primaryDisabled}
                className={`py-1 px-3 font-medium ${primaryButtonClass} text-white rounded-lg cursor-pointer w-full flex items-center gap-2 justify-center border-2 disabled:border-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200`}
              >
                {isPrimaryLoading ? (
                  <div className="flex items-center gap-2">
                    <CircularProgress size={16} color="inherit" />{" "}
                    <span className="text-sm">Memproses...</span>{" "}
                  </div>
                ) : (
                  <span>{PRIMARY_LABEL_BY_ACTION[action] ?? "Konfirmasi"}</span>
                )}
              </button>
            </div>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ModalConfirmStatus;
